import { CartItemDetails } from '@customTypes/cart-items';
import Product from '@customTypes/product';
import { numberOfAvailableUnits } from '@helpers/product';
import { useEffect, useMemo, useState } from 'react';
import pluralize from 'pluralize-esm';

export default function useCounterValidation(
  adultCounter: number,
  childrenCounter: number,
  cartData: CartItemDetails | undefined,
  product: Product,
  isQuantitySelector?: boolean | undefined,
) {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

  const noAvailableUnits = (personType: string, availableUnits: number) =>
    `Only ${availableUnits || product.adult_available_unit} ${personType} ${pluralize(
      product.name.toLowerCase(),
      availableUnits || product.adult_available_unit,
    )} left`;

  const maxChildrenPerAdultError = useMemo(
    () =>
      `This ${product.name.toLowerCase()} does not allow more than ${
        product.max_children_per_adult
      } ${pluralize('child', product.max_children_per_adult)} per adult`,
    [product],
  );

  const maxPersonTypeOccupancyError = (personType: string) =>
    `This ${product.name.toLowerCase()} has a maximum occupancy of ${
      personType === 'adults' ? product.max_adults_occupancy || 0 : product.max_children_occupancy
    } ${personType}`;

  const maxPersonOccupancyError = `This ${product.name.toLowerCase()} has a maximum occupancy of ${
    product.max_person_occupancy ?? 0
  }`;

  const childrenWithoutAnAdultError = 'Please add at least 1 adult prior to adding children';

  const quantityLimitReached = (availableUnits: number) =>
    `Only ${availableUnits} ${pluralize(product.name.toLowerCase(), availableUnits)} left`;

  const counterValidation = (futureValue: number, personType: string) => {
    const showErrorMessageAndReturn = (error: string) => {
      setShowErrorMessage(true);
      setErrorMessage(error);
      return false;
    };
    const availableUnits = numberOfAvailableUnits(product, cartData);
    if (isQuantitySelector) {
      if (futureValue >= availableUnits) {
        return showErrorMessageAndReturn(quantityLimitReached(availableUnits));
      }

      return true;
    }

    if (product.individual_billing) {
      if (personType === 'adults') {
        if (futureValue >= (availableUnits || product.adult_available_unit)) {
          return showErrorMessageAndReturn(noAvailableUnits(personType, availableUnits));
        }

        return true;
      }

      if (adultCounter === 0) {
        return showErrorMessageAndReturn(childrenWithoutAnAdultError);
      }

      if (futureValue >= adultCounter * product.max_children_per_adult) {
        return showErrorMessageAndReturn(maxChildrenPerAdultError);
      }

      return true;
    }

    if (!product.individual_billing) {
      if (adultCounter + childrenCounter >= product.max_person_occupancy) {
        return showErrorMessageAndReturn(maxPersonOccupancyError);
      }

      if (personType === 'adults') {
        if (futureValue >= product.max_adults_occupancy) {
          return showErrorMessageAndReturn(maxPersonTypeOccupancyError(personType));
        }

        return true;
      }

      if (adultCounter === 0) {
        return showErrorMessageAndReturn(childrenWithoutAnAdultError);
      }

      if (futureValue >= product.max_children_occupancy) {
        return showErrorMessageAndReturn(maxPersonTypeOccupancyError(personType));
      }

      return true;
    }

    return true;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowErrorMessage(false);
    }, 4000);

    return () => clearTimeout(timer);
  }, [showErrorMessage]);

  return {
    counterValidation,
    errorMessage,
    showErrorMessage,
  };
}
